import styled from '@emotion/styled';
// The file, not the barrel: the barrel also loads `responsive`, which reads `window` as it is imported.
import { Variables } from '../variables/variables';

interface IToggleProps {
    disabled: boolean;
    /** Whether the switch is on. Named `on`, not `value`, so it is not passed to the DOM (see `shouldForwardProp`). */
    on: boolean;
}

/**
 * A native `<button role="switch">`: it takes keyboard focus, switches with Space or Enter, and is announced as a
 * switch with its on/off state. The visual is unchanged from the old click-only div, so the button's own chrome is
 * reset here.
 */
export const Toggle = styled('button', { shouldForwardProp: (prop: string) => prop !== 'on' })<IToggleProps>`
    --toggle-bgcolor-disabled: ${Variables.Colors.n5};
    --toggle-bgcolor-on: ${Variables.Swatches.Blue5};
    --toggle-bgcolor-off: ${Variables.Colors.n30};
    --toggle-offset: ${(props: IToggleProps): string => (props.on ? '18px' : '2px')};
    --toggle-bgcolor: ${(props: IToggleProps): string =>
        props.disabled
            ? 'var(--toggle-bgcolor-disabled)'
            : props.on
            ? 'var(--toggle-bgcolor-on)'
            : 'var(--toggle-bgcolor-off)'};

    display: flex;
    height: 20px;
    width: 36px;
    // The knob is positioned by margin, so a shrunk track collapses to a circle when off and stays
    // roughly the right size when on — the width has to hold inside a flex row.
    flex-shrink: 0;
    border-radius: 20px;

    align-items: center;
    background-color: var(--toggle-bgcolor);
    cursor: pointer;

    /* Reset the button chrome so it looks like the track it replaced. */
    padding: 0;
    margin: 0;
    border: 0;
    font: inherit;
    appearance: none;

    &:disabled {
        cursor: default;
    }

    /* Keyboard users need to see where they are; mouse users do not get the ring. */
    &:focus-visible {
        outline: 2px solid ${Variables.ThemeLight.PrimaryInverted3};
        outline-offset: 2px;
    }
`;

/** A span, not a div: a button may only contain phrasing content. */
export const Knob = styled.span`
    display: block;
    height: 16px;
    width: 16px;
    border-radius: 16px;
    margin-left: var(--toggle-offset);
    box-shadow: 0 0 0 1px var(--toggle-bgcolor);
    background-color: ${Variables.Colors.white};
    pointer-events: none;

    transition: all 0.2s ease-in-out;
`;
